import {
  Box,
  chakra,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from "@chakra-ui/react";
import { useMemo } from "react";

interface DateFieldProps {
  name?: string;
  value?: string;
  label?: React.ReactNode;
  validationMessage?: React.ReactNode;
  onChange: (value: string) => void;
}

const selectStyle = {
  border: 0,
  background: "transparent",
  outline: "none",
  fontSize: "1rem",
  cursor: "pointer",
  padding: "0 0.25rem",
} as const;

export const DateField = ({
  onChange,
  value,
  label,
  validationMessage,
}: DateFieldProps) => {
  const currentYear = new Date().getFullYear();
  const valueString = value || "0000-00-00";

  const year = useMemo(() => valueString.substring(0, 4), [valueString]);
  const month = useMemo(() => valueString.substring(5, 7), [valueString]);
  const day = useMemo(() => valueString.substring(8), [valueString]);

  return (
    <FieldRoot invalid={!!validationMessage}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <Box
        borderWidth="2px"
        borderColor="brand.green.dark"
        borderStyle="solid"
        borderRadius="5px"
        alignItems="center"
        height="2.875rem"
        display="inline-flex"
        gap="0.5rem"
        paddingX="0.5rem"
        background="#ffffff"
      >
        <chakra.select
          value={month}
          onChange={(e) =>
            onChange(
              valueString.substring(0, 5) +
                e.target.value +
                valueString.substring(7),
            )
          }
          maxWidth="5rem"
          {...selectStyle}
        >
          <option value="00">MM</option>
          {Array.from({ length: 12 }, (_e, i) => i + 1).map((month) => {
            const m = month.toString().padStart(2, "0");
            return (
              <option value={m} key={m}>
                {m}
              </option>
            );
          })}
        </chakra.select>
        <chakra.select
          value={day}
          onChange={(e) =>
            onChange(valueString.substring(0, 8) + e.target.value)
          }
          maxWidth="5rem"
          {...selectStyle}
        >
          <option value="00">DD</option>
          {Array.from({ length: 31 }, (_e, i) => i + 1).map((day) => {
            const d = day.toString().padStart(2, "0");
            return (
              <option value={d} key={d}>
                {d}
              </option>
            );
          })}
        </chakra.select>
        <chakra.select
          value={year}
          onChange={(e) => onChange(e.target.value + valueString.substring(4))}
          maxWidth="8rem"
          width="8rem"
          {...selectStyle}
        >
          <option value="0000">YYYY</option>
          {Array.from({ length: 90 }, (_e, i) => i).map((delta) => {
            const year = (currentYear - delta - 10).toString();
            return (
              <option value={year} key={year}>
                {year}
              </option>
            );
          })}
        </chakra.select>
      </Box>
      {validationMessage && <FieldErrorText>{validationMessage}</FieldErrorText>}
    </FieldRoot>
  );
};
